import { brandLinka } from '@ads/tokens';
import { DashboardTickets } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatNumber } from '@linka/utils';
import { Label, Pie, PieChart } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
  IndicatorCard,
} from '../components';

interface TicketsGeneralChartProps {
  data: DashboardTickets;
}
export const TicketsGeneralChart = ({ data }: TicketsGeneralChartProps) => {
  const t = useTranslation();
  const chartData = [
    { name: 'sold', value: data.sold, fill: 'var(--color-sold)' },
    {
      name: t('remaining', { count: 1 }),
      value: Math.max(data.remainingTickets ?? 0, 0),
      fill: 'var(--color-remainingTickets)',
    },
  ];
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sold-vs-available')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_TICKETS_GENERAL' />
      </ChartCardHeader>
      <ChartCardContent className='relative'>
        <ChartContainer
          config={{
            sold: { color: brandLinka.baseColor.brand['400'], label: t('sold', { count: 2 }) },
            remainingTickets: { color: brandLinka.baseColor.primary['400'], label: t('remaining', { count: 2 }) },
          }}
          className='min-h-[200px] h-[200px] w-full'
        >
          <PieChart>
            <Pie
              data={chartData}
              startAngle={180}
              endAngle={0}
              innerRadius={80}
              outerRadius={140}
              dataKey='value'
              strokeWidth={0}
              paddingAngle={2}
              cx='50%'
              cy='80%'
            >
              <Label
                content={({ viewBox }) => {
                  if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) return null;
                  return (
                    <text textAnchor='middle'>
                      <tspan x={viewBox.cx} y={viewBox.cy + 40} className='fill-ctx-content-title ts-heading-lg'>
                        {formatNumber(data.sold)}
                      </tspan>
                      <tspan x={viewBox.cx} y={viewBox.cy + 58} className='fill-ctx-content-base ts-paragraph-xxxs'>
                        {t('tickets')}
                      </tspan>
                    </text>
                  );
                }}
              />
            </Pie>

            <ChartTooltip content={<ChartTooltipContent />} />
          </PieChart>
        </ChartContainer>
        <ChartCardFooter className='flex justify-center gap-nano flex-wrap *:w-full sm:*:w-[30%]'>
          <IndicatorCard label={t('available-quantity')} value={data.total ?? t('unlimited')} />
          <IndicatorCard label={t('sold-so-far')} value={data.sold} />
          <IndicatorCard label={t('remaining-quantity')} value={data.remainingTickets ?? t('unlimited')} />
          <IndicatorCard
            label={t('percentage-sold')}
            value={data.salesPercentage ?? t('not-applicable')}
            type='percent'
          />
          <IndicatorCard
            label={t('percentage-remaining')}
            value={data.salesPercentage ? Number(100 - data.salesPercentage) : t('not-applicable')}
            type='percent'
          />
        </ChartCardFooter>
      </ChartCardContent>
    </ChartCard>
  );
};
